/* =============================================================
   Rise Nutrition — Shopify theme JS
   No dependencies. Handles:
   - Mobile drawer open/close
   - Hero transparent nav (on home page)
   - Scroll reveal animations
   - AJAX quick-add to cart
   - Cart badge update
   - Toast notifications
   - FAQ accordion
   - Story video lightbox
   ============================================================= */

(function () {
  'use strict';

  /* ── Utility ─────────────────────────────────────────────── */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ── DOM ready ───────────────────────────────────────────── */

  document.documentElement.classList.remove('no-js');

  function init() {
    initNavDropdowns();
    initDrawer();
    initTransparentNav();
    initReveal();
    initQuickAdd();
    initCartBadge();
    initToast();
    initFaq();
    initStoryVideo();
    initQtySteppers();
  }

  /* ── Nav dropdowns ──────────────────────────────────────── */

  function initNavDropdowns() {
    document.querySelectorAll('.nav-drop').forEach(drop => {
      const btn = drop.querySelector('.nav-drop-btn');
      if (!btn) return;
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const isOpen = drop.classList.contains('is-open');
        document.querySelectorAll('.nav-drop.is-open').forEach(d => {
          d.classList.remove('is-open');
          d.querySelector('.nav-drop-btn').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          drop.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
    document.addEventListener('click', () => {
      document.querySelectorAll('.nav-drop.is-open').forEach(d => {
        d.classList.remove('is-open');
        d.querySelector('.nav-drop-btn').setAttribute('aria-expanded', 'false');
      });
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.nav-drop.is-open').forEach(d => {
          d.classList.remove('is-open');
          d.querySelector('.nav-drop-btn').setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  /* ── Mobile drawer ───────────────────────────────────────── */

  function initDrawer() {
    const openBtn = $('#drawer-open');
    const closeBtn = $('#drawer-close');
    const drawer = $('#site-drawer');
    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('open');
      openBtn && openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      closeBtn && closeBtn.focus();
    }

    function closeDrawer() {
      drawer.classList.remove('open');
      openBtn && openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    openBtn && openBtn.addEventListener('click', openDrawer);
    closeBtn && closeBtn.addEventListener('click', closeDrawer);

    // Close on backdrop click / Escape
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });
    drawer.addEventListener('click', e => { if (e.target === drawer) closeDrawer(); });
  }

  /* ── Transparent hero nav ────────────────────────────────── */

  function initTransparentNav() {
    const nav = $('#site-header');
    if (!nav || !nav.classList.contains('js-nav')) return;

    function updateNav() {
      const scrolled = window.scrollY > 40;
      if (scrolled) {
        nav.classList.remove('over');
        nav.classList.add('solid');
      } else {
        nav.classList.remove('solid');
        nav.classList.add('over');
      }
    }

    // Run immediately to correct any SSR/scroll mismatch
    updateNav();
    window.addEventListener('scroll', updateNav, { passive: true });
  }

  /* ── Scroll reveal ───────────────────────────────────────── */

  function initReveal() {
    const els = $$('.reveal');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(el => el.classList.add('in'));
      return;
    }

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    els.forEach(el => obs.observe(el));
  }

  /* ── Cart badge ──────────────────────────────────────────── */

  const cartCount = $('#cart-count');

  async function fetchCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const cart = await res.json();
      updateCartBadge(cart.item_count);
    } catch (e) { /* silent */ }
  }

  function updateCartBadge(count) {
    if (!cartCount) return;
    cartCount.textContent = count;
    cartCount.classList.toggle('show', count > 0);
    const link = $('#cart-icon-link');
    if (link) link.setAttribute('aria-label', `Cart (${count})`);
  }

  function initCartBadge() { fetchCartCount(); }

  /* Expose for product page inline script */
  window.riseCart = { refresh: fetchCartCount };

  /* ── Toast ───────────────────────────────────────────────── */

  let toastTimer;
  const toastEl = $('#site-toast');

  function showToast(message, cartUrl) {
    if (!toastEl) return;
    toastEl.innerHTML = message + (cartUrl ? ` <a href="${cartUrl}">View cart</a>` : '');
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 3500);
  }

  function initToast() { window.riseShowToast = showToast; }

  /* ── AJAX Quick-add ──────────────────────────────────────── */

  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.rn-quick-add');
      if (!btn) return;
      if (btn.disabled) return;

      const variantId = parseInt(btn.dataset.variantId);
      const title = btn.dataset.productTitle || 'Product';
      if (!variantId) return;

      btn.disabled = true;
      const origHTML = btn.innerHTML;
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" fill="none" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || 'Cart error');
        }

        await fetchCartCount();
        showToast(`${title} added to cart`, '/cart');

        // Visual success tick
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" fill="none" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>';
        setTimeout(() => {
          btn.innerHTML = origHTML;
          btn.disabled = false;
        }, 1600);

      } catch (err) {
        btn.innerHTML = origHTML;
        btn.disabled = false;
        showToast(err.message || 'Could not add to cart');
      }
    });
  }

  /* ── FAQ accordion ───────────────────────────────────────── */

  function initFaq() {
    // Delegated on document once, so it survives section re-renders that
    // replace #faq-list and would otherwise drop per-button listeners.
    if (window.__faqDelegated) return;
    window.__faqDelegated = true;
    document.addEventListener('click', e => {
      const btn = e.target.closest && e.target.closest('.faq-toggle');
      if (!btn) return;
      const item = btn.closest('.faq-item');
      if (!item) return;
      const wasOpen = item.classList.contains('open');
      $$('.faq-item.open').forEach(i => {
        i.classList.remove('open');
        i.querySelector('.faq-toggle')?.setAttribute('aria-expanded', 'false');
      });
      if (!wasOpen) {
        item.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ── Story video lightbox ────────────────────────────────── */

  function initStoryVideo() {
    const btn = $('.js-story-play');
    const modal = $('#story-modal');
    const iframe = $('#story-iframe');
    if (!btn || !modal || !iframe) return;

    btn.addEventListener('click', () => {
      let url = btn.dataset.video || '';
      // Convert YouTube watch URL to embed URL
      url = url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
      if (url && !url.includes('embed')) url = url;
      iframe.src = url + (url.includes('?') ? '&' : '?') + 'autoplay=1';
      modal.style.display = 'flex';
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.style.display === 'flex') {
        modal.style.display = 'none';
        iframe.src = '';
      }
    });
  }

  /* ── Qty steppers (generic) ──────────────────────────────── */

  function initQtySteppers() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.qty-minus, .qty-plus');
      if (!btn) return;
      const wrapper = btn.closest('.qty');
      if (!wrapper) return;
      const input = wrapper.querySelector('input[type="number"]');
      if (!input) return;
      const delta = btn.classList.contains('qty-plus') ? 1 : -1;
      const newVal = Math.max(parseInt(input.min) || 1, parseInt(input.value || 1) + delta);
      input.value = newVal;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ── Collection filter tabs ──────────────────────────────── */

  function applyCollectionFilter(filter) {
    $$('.shopbar .chip').forEach(c => c.classList.remove('active'));
    const match = $$('.shopbar .chip').find(c => (c.dataset.filter || '') === (filter || ''));
    if (match) match.classList.add('active');
    else { const first = $$('.shopbar .chip')[0]; if (first) first.classList.add('active'); }
    const banner = document.querySelector('#collection-grid .grid-banner-img');
    $$('#collection-grid .cell').forEach(card => {
      if (card.classList.contains('grid-banner-img')) return;
      if (!filter || filter === 'all') {
        card.style.display = '';
      } else {
        const cat = card.dataset.productType || '';
        card.style.display = cat === filter ? '' : 'none';
      }
    });
    if (banner) banner.style.display = (filter && filter !== 'all') ? 'none' : '';
    const countEl = document.getElementById('shop-count');
    if (countEl) {
      const vis = $$('#collection-grid .cell').filter(c => !c.classList.contains('grid-banner-img') && c.style.display !== 'none').length;
      countEl.textContent = vis + ' product' + (vis !== 1 ? 's' : '');
    }
  }

  $$('.shopbar .chip').forEach(chip => {
    chip.addEventListener('click', () => applyCollectionFilter(chip.dataset.filter));
  });

  // Auto-apply from URL ?type= on page load
  const urlType = new URLSearchParams(window.location.search).get('type');
  if (urlType && $$('#collection-grid').length) {
    applyCollectionFilter(urlType);
  }

  /* ── Boot ────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ── Stacks button injection (homepage product-grid filter bar) ── */
(function() {
  function injectStacksBtn() {
    var bar = document.querySelector('.cat-filters');
    if (!bar) return;
    if (bar.querySelector('.cat-btn--stacks')) return;
    var allBtn = bar.querySelector('[data-filter="all"]');
    if (!allBtn) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cat-btn cat-btn--stacks';
    btn.textContent = 'Stacks';
    btn.addEventListener('click', function() { window.location = '/collections/stacks'; });
    allBtn.insertAdjacentElement('afterend', btn);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectStacksBtn);
  } else {
    injectStacksBtn();
  }
  // Watch for theme re-renders
  var range = document.querySelector('.range');
  if (range && window.MutationObserver) {
    new MutationObserver(injectStacksBtn).observe(range, { childList: true, subtree: true });
  }
})();
